import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import Swipeable from 'react-swipeable';
import { CombinedImage } from '@ynap/image';
import HtmlVideoPlayer from '@ynap/video';
import ViewportObserver from '@ynap/viewport-observer';
import Overlay from '@ynap/overlay';
import ZoomedImageCarousel from '../ZoomedImageCarousel/ZoomedImageCarousel';
import ZoomedImage from '../ZoomedImage/ZoomedImage';
import { withMessages } from '@ynap/immutables-provider';
import { withOptimizely } from '@ynap/optimizely-utils';

import BEMHelper from '@ynap/bem-helper';

const bem = new BEMHelper('ImageCarousel');

//TODO thumbnail images could be used for progressive loading of main carousel image i.e. low res then high res on initial page load

export class ImageCarousel extends PureComponent {
    static defaultProps = {
        defaultIndex: 0,
        transition: 'transform 0.3s ease-out',
        alwaysFullWidth: false,
        paginationStacked: false,
        transitionDuration: 300,
        showThumbnails: false,
        allow1stLevelZoom: false,
        allow2ndLevelZoom: false,
        videos: [],
        images: [],
        videosFirst: false,
        ssr: false,
        dir: 'ltr',
        hideMobileArrow: false,
        showHint: false,
    };

    constructor(props) {
        super(props);
        const {
            defaultIndex,
            images,
            videos,
            loading,
            imageSrcsetSize,
            thumbnailsSrcsetSize,
            computeAttributes,
            videosFirst,
            ssr,
            dir,
            reducedMode,
            vertical,
        } = this.props;
        const { numberRealSlides, slides, zoomSlides } = generateImagesAndVideoArray(
            images,
            loading,
            imageSrcsetSize,
            videos,
            computeAttributes,
            videosFirst,
            null,
            reducedMode,
            vertical,
        );

        // get video thumbnails from video array
        const videoThumbnails =
            videos &&
            videos.reduce((acc, video) => {
                return [
                    ...acc,
                    {
                        modifier: 'video',
                        ...video.thumbnail,
                    },
                ];
            }, []);

        const thumbnails = generateImagesAndVideoArray(
            videosFirst ? [...videoThumbnails, ...images] : [...images, ...videoThumbnails],
            loading,
            thumbnailsSrcsetSize,
            undefined,
            computeAttributes,
            videosFirst,
            ssr,
            reducedMode,
            vertical,
        );

        this.state = {
            slides,
            zoomSlides,
            numberRealSlides,
            pageWidth: 100 / slides.length,
            trackWidth: slides.length * 100,
            currentIndex: defaultIndex,
            diffIndex: 0,
            leftMarginNeeded: 0,
            delta: 0,
            animating: false,
            thumbnails: newOrder(thumbnails.slides, defaultIndex, 0),
            currentOrder: newOrder(slides, defaultIndex),
            isZoomModalVisible: false,
            isRTL: dir === 'rtl',
            zoomedPosition: { fx: 0.5, fy: 0.5 },
            selectedZoomImageIndex: undefined,
            shouldRotateHTWIIcon: false,
            hasTrackedVTO: false,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { defaultIndex, images, videos, loading, imageSrcsetSize, thumbnailsSrcsetSize, computeAttributes, videosFirst, reducedMode, vertical } = nextProps;
        const { numberRealSlides, slides, zoomSlides } = generateImagesAndVideoArray(
            images,
            loading,
            imageSrcsetSize,
            videos,
            computeAttributes,
            videosFirst,
            null,
            reducedMode,
            vertical,
        );
        const videoThumbnails =
            videos &&
            videos.reduce((acc, video) => {
                return [
                    ...acc,
                    {
                        modifier: 'video',
                        ...video.thumbnail,
                    },
                ];
            }, []);
        const thumbnails = generateImagesAndVideoArray(
            videosFirst ? [...videoThumbnails, ...images] : [...images, ...videoThumbnails],
            loading,
            thumbnailsSrcsetSize,
            undefined,
            computeAttributes,
            videosFirst,
            undefined,
            reducedMode,
            vertical,
        );
        const currentIndexDiff = this.state.currentIndex > thumbnails.slides.length - 1 ? 0 : this.state.currentIndex;

        this.setState({
            slides,
            zoomSlides,
            numberRealSlides,
            pageWidth: 100 / slides.length,
            trackWidth: slides.length * 100,
            currentIndex: currentIndexDiff,
            diffIndex: 0,
            leftMarginNeeded: 0,
            delta: 0,
            animating: false,
            thumbnails: newOrder(thumbnails.slides, defaultIndex, 0),
            currentOrder: newOrder(slides, currentIndexDiff),
            isZoomModalVisible: false,
        });
    }

    moveRight = (moveLength, currentIndex) => {
        const { onInteraction = () => {}, goNext = () => {} } = this.props;
        this.setState(
            (prevState) => {
                return {
                    ...prevState,
                    currentIndex: currentIndex,
                    diffIndex: moveLength,
                    delta: 0,
                    leftMarginNeeded: 0,
                    animating: true,
                };
            },
            () => {
                setTimeout(() => {
                    onInteraction();
                    this.setState((prevState) => {
                        return {
                            ...prevState,
                            diffIndex: 0,
                            animating: false,
                            currentOrder: newOrder(prevState.slides, prevState.currentIndex),
                        };
                    });
                    goNext();
                }, this.props.transitionDuration);
            },
        );
    };

    moveLeft = (moveLength, currentIndex) => {
        const { onInteraction = () => {}, goPrev = () => {} } = this.props;
        this.setState(
            (prevState) => {
                return {
                    ...prevState,
                    currentIndex: currentIndex,
                    diffIndex: -moveLength,
                    leftMarginNeeded: -moveLength,
                    delta: 0,
                    animating: true,
                    currentOrder: newOrder(prevState.slides, currentIndex),
                };
            },
            () => {
                setTimeout(() => {
                    onInteraction();
                    this.setState((prevState) => ({
                        ...prevState,
                        diffIndex: 0,
                        leftMarginNeeded: 0,
                        delta: 0,
                        animating: false,
                    }));
                    goPrev();
                }, this.props.transitionDuration);
            },
        );
    };

    slideTo(index, circular = false) {
        const { slides, animating } = this.state;

        if (animating) {
            return;
        }

        let currentIndex;

        currentIndex = index < 0 || index >= slides.length ? slides.length - Math.abs(index) : index;

        const currentIndexDiff =
            Math.abs(currentIndex - this.state.currentIndex) >= slides.length - 1 && circular
                ? currentIndex - this.state.currentIndex > 0
                    ? -1
                    : 1
                : currentIndex - this.state.currentIndex;

        if (currentIndexDiff > 0) {
            this.moveRight(Math.abs(currentIndexDiff), currentIndex);
        } else if (currentIndexDiff < 0) {
            this.moveLeft(Math.abs(currentIndexDiff), currentIndex);
        }
    }

    getSlideStyle = (first = false) => {
        const { pageWidth, leftMarginNeeded } = this.state;

        return {
            width: `${pageWidth}%`,
            marginLeft: first && leftMarginNeeded ? `${leftMarginNeeded * pageWidth}%` : 0,
        };
    };

    getTrackStyle = () => {
        const { transition } = this.props;
        const { delta, trackWidth, pageWidth, diffIndex, isRTL } = this.state;
        const nonTransform = this.state.slides.length === 1;

        const transformNegative = isRTL ? '' : '-';

        return {
            width: `${trackWidth}%`,
            maxWidth: `${trackWidth}%`,
            transition: this.state.animating ? transition : 'none',
            transform: nonTransform
                ? 'none'
                : delta === 0
                  ? `translateX(${transformNegative}${pageWidth * (diffIndex + 2)}%)`
                  : `translateX(calc(${transformNegative}${pageWidth * (diffIndex + 2)}% + ${delta}px))`,
        };
    };

    prev = () => {
        const { currentIndex } = this.state;
        this.slideTo(currentIndex - 1, true);
    };

    next = () => {
        const { currentIndex } = this.state;
        this.slideTo(currentIndex + 1, true);
    };

    handleSwipingLeft = (event, delta) => {
        if (event.cancelable) {
            event.preventDefault();
        }
        this.setState({
            delta: -addResistance(delta),
        });
    };

    handleSwipingRight = (event, delta) => {
        if (event.cancelable) {
            event.preventDefault();
        }
        this.setState({
            delta: addResistance(delta),
        });
    };

    handleSwiped = (event, deltaX, deltaY) => {
        const { currentIndex, isRTL } = this.state;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX < 0) {
                return isRTL ? this.next() : this.prev();
            } else if (deltaX > 0) {
                return isRTL ? this.prev() : this.next();
            }
        }

        return this.slideTo(currentIndex, true);
    };

    toggleZoomModal = (selectedIndex) => {
        this.setState({
            isZoomModalVisible: !this.state.isZoomModalVisible,
            selectedZoomImageIndex: selectedIndex,
        });
    };

    onZoomedImageClick = (zoomedPosition) => {
        this.setState({
            zoomedPosition,
        });
    };

    handleVirtualTryOn = () => {
        const { currentOrder, currentIndex, hasTrackedVTO } = this.state;
        const { optimizely } = this.props;

        const imageId = currentOrder?.find?.((singleImageProps) => singleImageProps.index === currentIndex)?.component?.key?.substring(0, 2);
        !hasTrackedVTO && imageId && optimizely?.track?.(`${imageId}_click_tryon`);

        this.setState({
            hasTrackedVTO: true,
        });
    };

    applyVirtualTryOnScript = () => {
        const { isVTOEnabled, vtoScriptUrl, vtoCssUrl } = this.props;

        if (isVTOEnabled && !document.getElementById('VTOScript')) {
            const VTOScript = document.createElement('script');
            VTOScript.setAttribute('id', 'VTOScript');
            VTOScript.setAttribute('defer', 'defer');
            VTOScript.setAttribute('src', vtoScriptUrl);
            document.head.appendChild(VTOScript);

            const VTOCss = document.createElement('link');
            VTOCss.setAttribute('id', 'VTOCss');
            VTOCss.setAttribute('href', vtoCssUrl);
            VTOCss.setAttribute('rel', 'stylesheet');
            document.head.appendChild(VTOCss);
        }
    };

    componentDidMount() {
        this.applyVirtualTryOnScript();
    }

    componentDidUpdate() {
        this.applyVirtualTryOnScript();
    }

    componentWillUnmount() {
        if (document.getElementById('VTOScript')) {
            document.getElementById('VTOScript').remove();
            document.getElementById('VTOCss').remove();
        }
    }

    renderImageCarousel = () => {
        const {
            className = {},
            alwaysFullWidth,
            paginationStacked,
            loading,
            showThumbnails,
            allow1stLevelZoom,
            allow2ndLevelZoom,
            singleHero = false,
            Pagination,
            hideArrow,
            hideMobileArrow,
            images,
            NAPImageCarouselModifier,
            padding = '',
            messages,
            showHint,
            isVTOEnabled = false,
            reducedMode,
            colourPartNumber = undefined,
            vertical,
            soldOut,
        } = this.props;

        const { currentIndex, slides, zoomSlides, animating, numberRealSlides, currentOrder, thumbnails, isZoomModalVisible, isRTL, selectedZoomImageIndex } =
            this.state;
        const { carouselClassName = className, thumbnailsClassName = null, thumbnailClassName = null } = className;

        const nextImage = messages?.imageCarousel?.nextImage();
        const prevImage = messages?.imageCarousel?.prevImage();

        if (!loading && numberRealSlides === 0) {
            return <div />; // TODO: EMPTY CAROUSEL CASE
        }
        const hidden = hideArrow;
        const mobileHidden = hideMobileArrow;
        const single = images.length === 1;
        const withHint = images.length > 1 && showHint;
        const showPagination = images.length > 1;

        return (
            <Fragment>
                {showThumbnails && (
                    <div className={bem('thumbnails', { loading }, thumbnailsClassName)}>
                        {thumbnails
                            .filter((thumbnail) => !thumbnail.replica)
                            .map((ignore, i) =>
                                loading ? (
                                    <div key={i} className={bem('thumbnail', { loading }, thumbnailClassName)} />
                                ) : (
                                    <div
                                        key={`${thumbnails[i].component.key}replica${thumbnails[i].replica}`}
                                        className={bem('thumbnail', thumbnails[currentIndex].index === i ? 'active' : undefined, thumbnailClassName)}
                                        onClick={() => this.slideTo(currentIndex >= numberRealSlides ? numberRealSlides + i : i)}
                                    >
                                        {thumbnails[i].component}
                                    </div>
                                ),
                            )}
                    </div>
                )}

                <div
                    className={bem(
                        'mainCarouselWithDots',
                        { showThumbnails, loading, single, layoutDefaultView: !!NAPImageCarouselModifier, reducedMode },
                        carouselClassName,
                    )}
                >
                    {isZoomModalVisible && (
                        <div className={bem(null, null, className)}>
                            <Overlay
                                handleBodyClick={this.toggleZoomModal}
                                onClose={this.toggleZoomModal}
                                className={{ container: 'zoomedImageContainer', button: 'zoomedImageClose', content: 'zoomedImageContent' }}
                            >
                                <ZoomedImageCarousel
                                    slides={zoomSlides}
                                    currentIndex={selectedZoomImageIndex || 0}
                                    currentOrder={currentOrder}
                                    loading={loading}
                                    next={this.next}
                                    prev={this.prev}
                                    className={{ zoomedCarouselClassName: bem('zoomedImageCarousel') }}
                                    closeZoomModal={this.toggleZoomModal}
                                    isRTL={isRTL}
                                    zoomedPosition={this.state.zoomedPosition}
                                />
                            </Overlay>
                        </div>
                    )}
                    {Pagination && <Pagination className={bem('pagination')} goNext={this.next} goPrev={this.prev} />}
                    <div className={bem('mainCarousel', { allow2ndLevelZoom, reducedMode, vertical })}>
                        <button
                            className={bem('prev', { loading, hidden, mobileHidden, isRTL, reducedMode })}
                            type="button"
                            onClick={this.prev}
                            aria-label={prevImage}
                        ></button>

                        <Swipeable
                            className={bem('viewport', { 'full-width': alwaysFullWidth, loading, singleHero, withHint, reducedMode })}
                            onSwipingLeft={this.handleSwipingLeft}
                            onSwipingRight={this.handleSwipingRight}
                            onSwiped={this.handleSwiped}
                        >
                            <ul
                                className={bem('track', { loading })}
                                ref={(node) => {
                                    this.trackRef = node;
                                }}
                                style={this.getTrackStyle()}
                                data-testid="imagecarousel-main-image-track"
                            >
                                {currentOrder.map((slide, i) => (
                                    <li
                                        key={`${slide.component.key}replica${slide.replica}`}
                                        className={bem('slide', { noPadding: padding, withHint, reducedMode }, i === 2 ? 'active' : undefined)}
                                        style={this.getSlideStyle(i === 0)}
                                        onClick={() => allow2ndLevelZoom && !slide.video && this.toggleZoomModal(slide.index)} // We don't want videos to be zoomed
                                    >
                                        {allow1stLevelZoom && !slide.video && <ZoomedImage onClick={this.onZoomedImageClick} src={slide.component.props.src} />}
                                        {slide.component}
                                    </li>
                                ))}
                                {animating &&
                                    !alwaysFullWidth && [
                                        <li
                                            key={`${currentOrder[0].component.key}replica${currentOrder[0].replica}`}
                                            className={bem('slide', { withHint })}
                                            style={{
                                                ...this.getSlideStyle(false),
                                            }}
                                        >
                                            {currentOrder[0].component}
                                        </li>,
                                        <li
                                            key={`${currentOrder[1].component.key}replica${currentOrder[1].replica}`}
                                            className={bem('slide', { withHint })}
                                            style={{
                                                ...this.getSlideStyle(false),
                                            }}
                                        >
                                            {currentOrder[1].component}
                                        </li>,
                                    ]}
                            </ul>
                        </Swipeable>

                        <button
                            className={bem('next', { loading, hidden, mobileHidden, isRTL, reducedMode })}
                            type="button"
                            onClick={this.next}
                            aria-label={nextImage}
                        />
                        {isVTOEnabled && (
                            <div
                                className={bem('deep-gears-root')}
                                id="deep-gears-root"
                                data-outfit-id={`${colourPartNumber}@live`}
                                onClick={() => this.handleVirtualTryOn()}
                            />
                        )}
                    </div>
                    {vertical && (
                        <div className={bem('mainCarouselVertical')} data-testid="imagecarousel-vertical-image-track">
                            {slides?.map((slide) => (
                                <div
                                    key={slide.index}
                                    onClick={() => allow2ndLevelZoom && !slide.video && this.toggleZoomModal(slide.index)}
                                    className={bem('slide')}
                                >
                                    {allow1stLevelZoom && !slide.video && <ZoomedImage onClick={this.onZoomedImageClick} src={slide.component.props.src} />}
                                    {slide.component}
                                </div>
                            ))}
                        </div>
                    )}

                    {showPagination && (
                        <ul className={bem('pagination', { stacked: paginationStacked, showThumbnails, noPadding: !!padding, reducedMode, vertical, soldOut })}>
                            {slides
                                .filter((slide) => !slide.replica)
                                .map((ignore, i) => (
                                    <li key={i}>
                                        {loading ? (
                                            <div className={bem('page', { loading })}> {i + 1} </div>
                                        ) : (
                                            <div
                                                role="presentation"
                                                className={bem('page', slides[currentIndex].index === i ? 'active' : undefined)}
                                                onClick={() => this.slideTo(currentIndex >= numberRealSlides ? numberRealSlides + i : i)}
                                            >
                                                {i + 1}
                                            </div>
                                        )}
                                    </li>
                                ))}
                        </ul>
                    )}
                </div>
            </Fragment>
        );
    };

    render() {
        const { className = {}, children } = this.props;
        const { isZoomModalVisible } = this.state;
        const { carouselGridClassName = null, carouselGridStickyClassName = null } = className;

        return (
            <>
                {carouselGridClassName && carouselGridStickyClassName ? (
                    <div className={bem(null, null, [isZoomModalVisible ? carouselGridClassName : carouselGridStickyClassName])}>
                        {this.renderImageCarousel()}
                        {children}
                    </div>
                ) : (
                    this.renderImageCarousel()
                )}
            </>
        );
    }
}

ImageCarousel.propTypes = {
    goNext: PropTypes.func,
    goPrev: PropTypes.func,
    singleHero: PropTypes.bool,
    Pagination: PropTypes.node,
    hideArrow: PropTypes.bool,
    hideMobileArrow: PropTypes.bool,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            url: PropTypes.string,
        }).isRequired,
    ),
    // TODO: ADD VIDEOS
    alwaysFullWidth: PropTypes.bool,
    defaultIndex: PropTypes.number,
    transition: PropTypes.string,
    paginationStacked: PropTypes.bool,
    transitionDuration: PropTypes.number,
    loading: PropTypes.bool,
    videos: PropTypes.any,
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    imageSrcsetSize: PropTypes.string,
    thumbnailsSrcsetSize: PropTypes.string,
    showThumbnails: PropTypes.bool,
    onInteraction: PropTypes.func,
    computeAttributes: PropTypes.bool,
    allow1stLevelZoom: PropTypes.bool,
    allow2ndLevelZoom: PropTypes.bool,
    videosFirst: PropTypes.bool,
    ssr: PropTypes.bool,
    dir: PropTypes.string,
    children: PropTypes.node,
    showHint: PropTypes.bool,
    messages: PropTypes.object.isRequired,
    NAPImageCarouselModifier: PropTypes.string,
    padding: PropTypes.string,
    optimizely: PropTypes.shape({
        track: PropTypes.func.isRequired,
    }),
    reducedMode: PropTypes.bool,
};

export const generateImagesAndVideoArray = (
    images = [],
    loading = false,
    imageSrcsetSize,
    videos,
    computeAttributes,
    videosFirst,
    ssr,
    reducedMode,
    vertical,
) => {
    /* empty images if imageCarousel is loading */
    images = images.length == 0 || loading ? [{ id: 0 }, { id: 1 }, { id: 2 }] : images;
    // TODO: Add video
    const slidesArray = [
        ...images.map((image, i) => ({
            replica: 0,
            index: i,
            component: (
                <CombinedImage
                    key={image.id + i}
                    className={{ imageContainer: bem('thumbnailImage', image.modifier), image: vertical ? 'fullBleed' : '' }}
                    src={image.url}
                    alt={image.alt}
                    primarySrcset={image.srcSet}
                    imageSrcsetSize={imageSrcsetSize}
                    displaySrc={true}
                    yOffset={'100%'}
                    computeAttributes={computeAttributes}
                    withSkeleton={!ssr && i > 0} //remove skeleton for first image on server side render only
                    withIntersectionObserver={i !== 0} //remove intersection observer for first image
                    aspectRatio={vertical ? 'ratio100vh' : null}
                />
            ),
        })),
    ];

    const zoomSlides = [
        ...images.map((image, i) => ({
            replica: 0,
            index: i,
            component: (
                <CombinedImage
                    key={image.id + i}
                    className={bem('thumbnailImage', image.modifier)}
                    src={image.zoomUrl}
                    alt={image.alt}
                    primarySrcset={image.zoomSrcSet}
                    imageSrcsetSize={'(min-width: 1440px) 100vw, (min-width: 768px) 50vw, 100vw'}
                    displaySrc={true}
                    yOffset={'100%'}
                    computeAttributes={computeAttributes}
                />
            ),
        })),
    ];

    videos &&
        slidesArray[videosFirst ? 'unshift' : 'push'](
            ...videos.map((video, index) => ({
                replica: 0,
                index: videosFirst ? index : slidesArray.length,
                video: true,
                component: (
                    <ViewportObserver threshold={[0.5]} keepObserving={true}>
                        {(intersected) => (
                            <HtmlVideoPlayer video={video} key={video.url} intersected={intersected} className={bem('video', reducedMode && 'reducedMode')} />
                        )}
                    </ViewportObserver>
                ),
            })),
        );

    if (slidesArray.length < 1) {
        return { numberRealSlides: 0, slides: [], zoomSlides: [] };
    }

    return slidesArray.length > 4 || vertical
        ? { numberRealSlides: slidesArray.length, slides: slidesArray, zoomSlides }
        : {
              numberRealSlides: slidesArray.length,
              slides: replicateSlides(slidesArray),
              zoomSlides: replicateSlides(zoomSlides),
          };
};

export default withOptimizely(withMessages()(ImageCarousel));

export const addResistance = (delta) => {
    return Math.round(delta * (1 - parseInt(Math.abs(delta), 10) / 1000));
};

export const replicateSlides = (
    slides,
    replicas = [
        ...slides.map((slide) => ({
            ...slide,
            replica: 1,
        })),
    ],
) => {
    if (slides.length >= 5) {
        return slides;
    }

    return replicateSlides(
        [...slides, ...replicas],
        replicas.map((slide) => ({
            ...slide,
            replica: slide.replica + 1,
        })),
    );
};

export const newOrder = (slides, currentIndex, shift = currentIndex - 2) => {
    if (shift === 0) {
        return [...slides];
    }

    if (shift > 0) {
        const [first, ...rest] = slides;
        return newOrder([...rest, first], currentIndex, shift - 1);
    }

    if (shift < 0) {
        const rest = [...slides];
        const lastItem = rest.pop();

        return newOrder([lastItem, ...rest], currentIndex, shift + 1);
    }
};
